/**
 * PLC 语言 Monaco 编辑器语法配置
 * 
 * 为 ST（结构化文本）和 IL（指令表）提供：
 * - 语法高亮（Token 定义）
 * - 自动补全
 * - 代码折叠
 * - 括号匹配
 */

import type * as Monaco from 'monaco-editor';
import type { PlcLanguageType } from '../../types';

// ============================================
// ST 语言配置（结构化文本）
// ============================================

/** ST 语言 Token 定义 */
export const ST_LANGUAGE_DEF: Monaco.languages.IMonarchLanguage = {
  defaultToken: '',
  ignoreCase: true,
  
  keywords: [
    'PROGRAM', 'END_PROGRAM',
    'FUNCTION', 'END_FUNCTION',
    'FUNCTION_BLOCK', 'END_FUNCTION_BLOCK',
    'VAR', 'VAR_INPUT', 'VAR_OUTPUT', 'VAR_IN_OUT', 'VAR_GLOBAL', 'VAR_TEMP', 'END_VAR',
    'CONSTANT', 'RETAIN', 'PERSISTENT',
    'IF', 'THEN', 'ELSIF', 'ELSE', 'END_IF',
    'CASE', 'OF', 'END_CASE',
    'FOR', 'TO', 'BY', 'DO', 'END_FOR',
    'WHILE', 'END_WHILE',
    'REPEAT', 'UNTIL', 'END_REPEAT',
    'RETURN', 'EXIT', 'CONTINUE',
    'AT', 'WITH', 'USING'
  ],
  
  typeKeywords: [
    'BOOL', 'BYTE', 'WORD', 'DWORD', 'LWORD',
    'SINT', 'INT', 'DINT', 'LINT',
    'USINT', 'UINT', 'UDINT', 'ULINT',
    'REAL', 'LREAL',
    'STRING', 'WSTRING', 'CHAR', 'WCHAR',
    'TIME', 'DATE', 'TIME_OF_DAY', 'DATE_AND_TIME',
    'TOD', 'DT',
    'ARRAY', 'STRUCT', 'END_STRUCT',
    'TYPE', 'END_TYPE',
    'POINTER', 'REF_TO'
  ],
  
  operators: [
    ':=', '=>', '..', 
    '+', '-', '*', '/', 
    '<', '>', '<=', '>=', '=', '<>',
    '&', 'AND', 'OR', 'XOR', 'NOT', 'MOD'
  ],
  
  builtinConstants: [
    'TRUE', 'FALSE', 'NULL'
  ],
  
  builtinFunctions: [
    // 数学函数
    'ABS', 'SQRT', 'LN', 'LOG', 'EXP', 'SIN', 'COS', 'TAN', 'ASIN', 'ACOS', 'ATAN',
    // 类型转换
    'BOOL_TO_INT', 'INT_TO_BOOL', 'INT_TO_REAL', 'REAL_TO_INT',
    'DINT_TO_REAL', 'REAL_TO_DINT', 'TIME_TO_DINT', 'DINT_TO_TIME',
    // 字符串函数
    'LEN', 'LEFT', 'RIGHT', 'MID', 'CONCAT', 'INSERT', 'DELETE', 'REPLACE', 'FIND',
    // 选择函数
    'SEL', 'MAX', 'MIN', 'LIMIT', 'MUX',
    // 位操作
    'SHL', 'SHR', 'ROL', 'ROR'
  ],
  
  symbols: /[=><!~?:&|+\-*\/\^%]+/,
  
  escapes: /\\(?:[abfnrtv\\"']|x[0-9A-Fa-f]{1,4}|u[0-9A-Fa-f]{4}|U[0-9A-Fa-f]{8})/,
  
  tokenizer: {
    root: [
      // 注释
      [/\(\*/, 'comment', '@comment'],
      [/\/\/.*$/, 'comment'],
      
      // 字符串
      [/'([^'\\]|\\.)*$/, 'string.invalid'],
      [/'/, 'string', '@string_single'],
      [/"([^"\\]|\\.)*$/, 'string.invalid'],
      [/"/, 'string', '@string_double'],
      
      // 数字
      [/\d*\.\d+([eE][\-+]?\d+)?/, 'number.float'],
      [/16#[0-9A-Fa-f_]+/, 'number.hex'],
      [/8#[0-7_]+/, 'number.octal'],
      [/2#[01_]+/, 'number.binary'],
      [/\d+/, 'number'],
      
      // 时间字面量
      [/T#[\d_hmsdu]+/i, 'number.time'],
      [/TIME#[\d_hmsdu]+/i, 'number.time'],
      [/D#\d{4}-\d{2}-\d{2}/i, 'number.date'],
      [/DATE#\d{4}-\d{2}-\d{2}/i, 'number.date'],
      [/TOD#\d{2}:\d{2}:\d{2}(\.\d+)?/i, 'number.time'],
      [/DT#\d{4}-\d{2}-\d{2}-\d{2}:\d{2}:\d{2}/i, 'number.datetime'],
      
      // 标识符和关键字
      [/[a-zA-Z_]\w*/, {
        cases: {
          '@keywords': 'keyword',
          '@typeKeywords': 'type',
          '@builtinConstants': 'constant',
          '@builtinFunctions': 'function.builtin',
          '@default': 'identifier'
        }
      }],
      
      // 分隔符和操作符
      [/[{}()\[\]]/, '@brackets'],
      [/[;,.]/, 'delimiter'],
      [/@symbols/, {
        cases: {
          '@operators': 'operator',
          '@default': ''
        }
      }]
    ],
    
    comment: [
      [/[^\(\*]+/, 'comment'],
      [/\*\)/, 'comment', '@pop'],
      [/[\(\*]/, 'comment']
    ],
    
    string_single: [
      [/[^'\\]+/, 'string'],
      [/@escapes/, 'string.escape'],
      [/\\./, 'string.escape.invalid'],
      [/'/, 'string', '@pop']
    ],
    
    string_double: [
      [/[^"\\]+/, 'string'],
      [/@escapes/, 'string.escape'],
      [/\\./, 'string.escape.invalid'],
      [/"/, 'string', '@pop']
    ]
  }
};

/** ST 语言配置 */
export const ST_LANGUAGE_CONF: Monaco.languages.LanguageConfiguration = {
  comments: {
    lineComment: '//',
    blockComment: ['(*', '*)']
  },
  brackets: [
    ['{', '}'],
    ['[', ']'],
    ['(', ')']
  ],
  autoClosingPairs: [
    { open: '{', close: '}' },
    { open: '[', close: ']' },
    { open: '(', close: ')' },
    { open: "'", close: "'", notIn: ['string', 'comment'] },
    { open: '"', close: '"', notIn: ['string', 'comment'] },
    { open: '(*', close: '*)' }
  ],
  surroundingPairs: [
    { open: '{', close: '}' },
    { open: '[', close: ']' },
    { open: '(', close: ')' },
    { open: "'", close: "'" },
    { open: '"', close: '"' }
  ],
  folding: {
    markers: {
      start: /^\s*(PROGRAM|FUNCTION|FUNCTION_BLOCK|VAR|IF|CASE|FOR|WHILE|REPEAT)\b/i,
      end: /^\s*(END_PROGRAM|END_FUNCTION|END_FUNCTION_BLOCK|END_VAR|END_IF|END_CASE|END_FOR|END_WHILE|END_REPEAT)\b/i
    }
  },
  indentationRules: {
    increaseIndentPattern: /^\s*(PROGRAM|FUNCTION|FUNCTION_BLOCK|VAR|IF.*THEN|ELSE|ELSIF.*THEN|CASE|FOR.*DO|WHILE.*DO|REPEAT)\s*$/i,
    decreaseIndentPattern: /^\s*(END_PROGRAM|END_FUNCTION|END_FUNCTION_BLOCK|END_VAR|END_IF|END_CASE|END_FOR|END_WHILE|END_REPEAT|ELSE|ELSIF|UNTIL)\b/i
  }
};

// ============================================
// IL 语言配置（指令表）
// ============================================

/** IL 语言 Token 定义 */
export const IL_LANGUAGE_DEF: Monaco.languages.IMonarchLanguage = {
  defaultToken: '',
  ignoreCase: true,
  
  instructions: [
    // 加载/存储
    'LD', 'LDN', 'ST', 'STN', 'S', 'R',
    // 逻辑操作
    'AND', 'ANDN', 'OR', 'ORN', 'XOR', 'XORN', 'NOT',
    // 算术操作
    'ADD', 'SUB', 'MUL', 'DIV', 'MOD', 'NEG',
    // 比较操作
    'GT', 'GE', 'EQ', 'NE', 'LE', 'LT',
    // 跳转/调用
    'JMP', 'JMPC', 'JMPCN', 'JMPNC',
    'CAL', 'CALC', 'CALCN', 'CALNC',
    'RET', 'RETC', 'RETCN', 'RETNC'
  ],
  
  modifiers: [
    'N', 'C', 'CN', 'NC'
  ],
  
  builtinConstants: [
    'TRUE', 'FALSE'
  ],
  
  tokenizer: {
    root: [
      // 注释
      [/\(\*/, 'comment', '@comment'],
      [/\/\/.*$/, 'comment'],
      
      // 标签（以冒号结尾）
      [/^[A-Za-z_]\w*:/, 'tag'],
      
      // 指令
      [/[A-Za-z_]\w*/, {
        cases: {
          '@instructions': 'keyword.instruction',
          '@builtinConstants': 'constant',
          '@default': 'identifier'
        }
      }],
      
      // 数字
      [/\d+\.\d*/, 'number.float'],
      [/\d+/, 'number'],
      [/16#[0-9A-Fa-f]+/, 'number.hex'],
      
      // 操作符和分隔符
      [/[()]/, '@brackets'],
      [/[,]/, 'delimiter']
    ],
    
    comment: [
      [/[^\(\*]+/, 'comment'],
      [/\*\)/, 'comment', '@pop'],
      [/[\(\*]/, 'comment']
    ]
  }
};

/** IL 语言配置 */
export const IL_LANGUAGE_CONF: Monaco.languages.LanguageConfiguration = {
  comments: {
    lineComment: '//',
    blockComment: ['(*', '*)']
  },
  brackets: [
    ['(', ')']
  ],
  autoClosingPairs: [
    { open: '(', close: ')' },
    { open: '(*', close: '*)' }
  ]
};

// ============================================
// ST 自动补全
// ============================================

/** 创建 ST 自动补全提供器 */
export const createStCompletionProvider = (
  monaco: typeof Monaco
): Monaco.languages.CompletionItemProvider => ({
  provideCompletionItems: (model, position) => {
    const word = model.getWordUntilPosition(position);
    const range: Monaco.IRange = {
      startLineNumber: position.lineNumber,
      endLineNumber: position.lineNumber,
      startColumn: word.startColumn,
      endColumn: word.endColumn
    };
    
    const suggestions: Monaco.languages.CompletionItem[] = [
      // 程序结构
      {
        label: 'PROGRAM',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'PROGRAM ${1:ProgramName}\nVAR\n    $0\nEND_VAR\n\nEND_PROGRAM',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: '创建一个新程序',
        range
      },
      {
        label: 'FUNCTION_BLOCK',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'FUNCTION_BLOCK ${1:FB_Name}\nVAR_INPUT\n    $2\nEND_VAR\nVAR_OUTPUT\n    $3\nEND_VAR\nVAR\n    $4\nEND_VAR\n\n$0\n\nEND_FUNCTION_BLOCK',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: '创建一个功能块',
        range
      },
      {
        label: 'FUNCTION',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'FUNCTION ${1:FunctionName} : ${2:BOOL}\nVAR_INPUT\n    $3\nEND_VAR\nVAR\n    $4\nEND_VAR\n\n$0\n\nEND_FUNCTION',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: '创建一个函数',
        range
      },
      
      // 变量声明
      {
        label: 'VAR',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'VAR\n    $0\nEND_VAR',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: '局部变量声明块',
        range
      },
      {
        label: 'VAR_INPUT',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'VAR_INPUT\n    $0\nEND_VAR',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: '输入变量声明块',
        range
      },
      {
        label: 'VAR_OUTPUT',
        kind: monaco.languages.CompletionItemKind.Keyword,
        insertText: 'VAR_OUTPUT\n    $0\nEND_VAR',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: '输出变量声明块',
        range
      },
      
      // 控制结构
      {
        label: 'IF-THEN-END_IF',
        kind: monaco.languages.CompletionItemKind.Snippet,
        insertText: 'IF ${1:condition} THEN\n    $0\nEND_IF;',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'IF 条件语句',
        range
      },
      {
        label: 'IF-THEN-ELSE-END_IF',
        kind: monaco.languages.CompletionItemKind.Snippet,
        insertText: 'IF ${1:condition} THEN\n    $2\nELSE\n    $0\nEND_IF;',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'IF-ELSE 条件语句',
        range
      },
      {
        label: 'CASE-OF-END_CASE',
        kind: monaco.languages.CompletionItemKind.Snippet,
        insertText: 'CASE ${1:selector} OF\n    ${2:1}:\n        $0\nEND_CASE;',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'CASE 分支语句',
        range
      },
      {
        label: 'FOR-TO-DO-END_FOR',
        kind: monaco.languages.CompletionItemKind.Snippet,
        insertText: 'FOR ${1:i} := ${2:0} TO ${3:10} DO\n    $0\nEND_FOR;',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'FOR 循环',
        range
      },
      {
        label: 'WHILE-DO-END_WHILE',
        kind: monaco.languages.CompletionItemKind.Snippet,
        insertText: 'WHILE ${1:condition} DO\n    $0\nEND_WHILE;',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'WHILE 循环',
        range
      },
      {
        label: 'REPEAT-UNTIL-END_REPEAT',
        kind: monaco.languages.CompletionItemKind.Snippet,
        insertText: 'REPEAT\n    $0\nUNTIL ${1:condition}\nEND_REPEAT;',
        insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
        documentation: 'REPEAT-UNTIL 循环',
        range
      },
      
      // 数据类型
      ...['BOOL', 'INT', 'DINT', 'REAL', 'LREAL', 'STRING', 'TIME', 'DATE'].map(type => ({
        label: type,
        kind: monaco.languages.CompletionItemKind.TypeParameter,
        insertText: type,
        documentation: `${type} 数据类型`,
        range
      }))
    ];
    
    return { suggestions };
  }
});

// ============================================
// 语言注册
// ============================================

/** 语言注册状态 */
let languagesRegistered = false;

/**
 * 注册 PLC 语言到 Monaco
 * 幂等操作，多次调用只注册一次
 */
export const registerPlcLanguages = (monaco: typeof Monaco): void => {
  if (languagesRegistered) {
    return;
  }
  
  // 注册 ST 语言
  monaco.languages.register({
    id: 'st',
    extensions: ['.st', '.stx'],
    aliases: ['Structured Text', 'ST', 'IEC 61131-3 ST']
  });
  monaco.languages.setMonarchTokensProvider('st', ST_LANGUAGE_DEF);
  monaco.languages.setLanguageConfiguration('st', ST_LANGUAGE_CONF);
  monaco.languages.registerCompletionItemProvider('st', createStCompletionProvider(monaco));
  
  // 注册 IL 语言
  monaco.languages.register({
    id: 'il',
    extensions: ['.il'],
    aliases: ['Instruction List', 'IL', 'IEC 61131-3 IL']
  });
  monaco.languages.setMonarchTokensProvider('il', IL_LANGUAGE_DEF);
  monaco.languages.setLanguageConfiguration('il', IL_LANGUAGE_CONF);
  
  // LAD 和 FBD 是图形化语言，使用纯文本模式
  monaco.languages.register({
    id: 'lad',
    extensions: ['.lad'],
    aliases: ['Ladder Diagram', 'LAD']
  });
  
  monaco.languages.register({
    id: 'fbd',
    extensions: ['.fbd'],
    aliases: ['Function Block Diagram', 'FBD']
  });
  
  languagesRegistered = true;
};

/**
 * 获取 Monaco 语言 ID
 */
export const getMonacoLanguageId = (language: PlcLanguageType): string => {
  // 直接返回，因为我们已注册了对应 ID
  return language;
};

// ============================================
// 主题配置
// ============================================

/** PLC 暗色主题 */
export const PLC_DARK_THEME: Monaco.editor.IStandaloneThemeData = {
  base: 'vs-dark',
  inherit: true,
  rules: [
    { token: 'keyword', foreground: '569CD6', fontStyle: 'bold' },
    { token: 'keyword.instruction', foreground: 'C586C0', fontStyle: 'bold' },
    { token: 'type', foreground: '4EC9B0' },
    { token: 'constant', foreground: '4FC1FF' },
    { token: 'function.builtin', foreground: 'DCDCAA' },
    { token: 'identifier', foreground: '9CDCFE' },
    { token: 'operator', foreground: 'D4D4D4' },
    { token: 'number', foreground: 'B5CEA8' },
    { token: 'number.float', foreground: 'B5CEA8' },
    { token: 'number.hex', foreground: 'B5CEA8' },
    { token: 'number.time', foreground: 'CE9178' },
    { token: 'number.date', foreground: 'CE9178' },
    { token: 'string', foreground: 'CE9178' },
    { token: 'string.escape', foreground: 'D7BA7D' },
    { token: 'comment', foreground: '6A9955', fontStyle: 'italic' },
    { token: 'tag', foreground: 'D7BA7D', fontStyle: 'bold' },
    { token: 'delimiter', foreground: 'D4D4D4' }
  ],
  colors: {
    'editor.background': '#1E1E1E',
    'editor.foreground': '#D4D4D4',
    'editorLineNumber.foreground': '#858585',
    'editorCursor.foreground': '#AEAFAD',
    'editor.selectionBackground': '#264F78',
    'editor.inactiveSelectionBackground': '#3A3D41'
  }
};

/**
 * 注册 PLC 主题
 */
export const registerPlcTheme = (monaco: typeof Monaco): void => {
  monaco.editor.defineTheme('plc-dark', PLC_DARK_THEME);
};